import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CForm,
  CFormInput,
  CFormLabel,
  CInputGroup,
} from "@coreui/react";
import config from "../config/config";
import { normalizeRole } from "../utils/roleMap";
import PublicAuthLayout from "../components/public/PublicAuthLayout";
import "../styles/public-experience.css";

const benefits = [
  {
    label: "Produktlogik",
    title: "Jobs, Qualifikationen und Unterlagen an einem Ort",
    text: "Bewerber und Arbeitgeber greifen auf dieselbe strukturierte Produktlogik für Recruiting und nächste Schritte zu.",
  },
  {
    label: "Einordnung",
    title: "Sprachstand, Wissen und Dokumente klar einordnen",
    text: "Sprachlogik, Wissenstests und vorbereitete Unterlagen bleiben nachvollziehbar statt unübersichtlich verteilt.",
  },
  {
    label: "Fortsetzung",
    title: "Recruiting- und Einwanderungsschritte geordnet fortsetzen",
    text: "Der Zugang führt in den passenden Arbeitsraum für Bewerbungen, Stellen, Tests und vorbereitete nächste Schritte.",
  },
];

function mapLoginError(error, email, password) {
  if (!String(email || "").trim() || !String(password || "").trim()) {
    return "Bitte geben Sie E-Mail und Passwort ein.";
  }

  const status = Number(error?.response?.status || 0);
  const serverMessage = String(error?.response?.data?.error || "").toLowerCase();

  if (status === 400 || serverMessage.includes("missing credentials")) {
    return "Bitte geben Sie E-Mail und Passwort ein.";
  }

  if (
    status === 401 ||
    status === 403 ||
    status === 423 ||
    serverMessage.includes("invalid credentials") ||
    serverMessage.includes("account is inactive") ||
    serverMessage.includes("account is locked")
  ) {
    return "Anmeldung nicht möglich. Bitte prüfen Sie Ihre Eingaben.";
  }

  return "Der Zugang ist im Moment nicht verfügbar. Bitte versuchen Sie es erneut.";
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    document.title = "Experts4Europe | Anmelden";
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    setErrorMsg("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${config.API_BASE_URL}/api/login`, { email, password });
      const user = response.data.user;
      const role = normalizeRole(user.role);

      localStorage.setItem("token", response.data.token);
      localStorage.setItem("userid", user.userid);
      localStorage.setItem("role", role);

      if (role === "admin") navigate("/dashboard/admin");
      else if (role === "applicant") navigate("/dashboard/applicant");
      else if (role === "employer") navigate("/dashboard/employer");
      else if (role === "press") navigate("/dashboard/press");
      else setErrorMsg("Der Zugang ist im Moment nicht verfügbar. Bitte versuchen Sie es erneut.");
    } catch (error) {
      setErrorMsg(mapLoginError(error, email, password));
    } finally {
      setIsLoading(false);
    }
  };

  const rightPane = (
    <CCard className="public-core-auth-card">
      <CCardBody>
        <div className="public-core-auth-card__head">
          <span className="public-core-eyebrow">Access Gateway</span>
          <h2>Anmelden</h2>
          <p>Zugang zu Ihrem Experts4Europe-Bereich für Bewerbungen, Stellen, Tests, Dokumente und nächste Schritte.</p>
        </div>

        <div className="public-core-role-context public-core-role-context--compact">
          <strong>Klare Rollenlogik</strong>
          <p>Bewerber, Arbeitgeber und freigeschaltete interne Zugänge werden direkt in den jeweils passenden Bereich geführt.</p>
        </div>

        <CForm className="public-core-form" onSubmit={handleLogin}>
          <div className="public-core-form-row">
            <CFormLabel htmlFor="login-email">E-Mail</CFormLabel>
            <CFormInput
              id="login-email"
              type="email"
              placeholder="name@beispiel.de"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="public-core-input"
            />
          </div>

          <div className="public-core-form-row">
            <CFormLabel htmlFor="login-password">Passwort</CFormLabel>
            <CInputGroup className="public-core-input-group">
              <CFormInput
                id="login-password"
                type={showPassword ? "text" : "password"}
                placeholder="Passwort eingeben"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="public-core-input"
              />
              <CButton
                type="button"
                className="public-core-toggle-btn"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? "Verbergen" : "Anzeigen"}
              </CButton>
            </CInputGroup>
          </div>

          <div className="public-core-form-hint">
            <span>Passwort kann direkt im Feld angezeigt oder verborgen werden.</span>
            <Link to="/menue/support">Bei Zugangsproblemen</Link>
          </div>

          {errorMsg ? (
            <CAlert color="danger" className="public-core-alert">
              {errorMsg}
            </CAlert>
          ) : null}

          <div className="public-core-form-actions">
            <CButton type="submit" className="public-core-btn public-core-btn--primary public-core-btn--block" disabled={isLoading}>
              {isLoading ? "Anmeldung läuft..." : "Login"}
            </CButton>
            <CButton
              type="button"
              className="public-core-btn public-core-btn--light public-core-btn--block"
              onClick={() => navigate("/")}
            >
              Zur Startseite
            </CButton>
          </div>
        </CForm>

        <div className="public-core-card-note">
          Noch kein Konto? <Link to="/register">Registrierung starten</Link>
        </div>
      </CCardBody>
    </CCard>
  );

  return (
    <PublicAuthLayout
      activeAuthPath="/login"
      eyebrow="Experts4Europe Zugang"
      title="Anmelden und den nächsten Schritt geordnet fortsetzen"
      description="Zugang für Bewerber, Arbeitgeber und freigeschaltete interne Bereiche. Der Login führt direkt in den jeweils passenden Experts4Europe-Bereich für Jobs, Qualifikationen, Tests, Dokumente und nächste Schritte."
      benefits={benefits}
      rightPane={rightPane}
    />
  );
}
